'use client';

import {
  useContext,
  createContext,
  useState,
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { debounce } from '@bts-web/utils-functions';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { CryptoTransfersVASPQuery$data } from '@bts-web/data-layer/server';
import { MINIMUM_SEARCH_TERM_LENGTH } from '../../../config/prices';
import { getPaginatedAssetsForTransfer } from '../../../common/gqlActions';
import { TAssetsForTransfersResponse } from '../TransfersLoader/types';
import {
  EmptyPaginatedAssetsForTransfersResponse,
  extractDepositAndSellAssets,
  getPaginatedAssetsForTransferInitialQueryParams,
} from '../TransfersLoader/utils';
import { datadogErrorHelper } from '../../../common';
import { useHandleGqlErrorsWithSnackbar } from '../../../common/utils/errors';
import { TAssetForTransferArray } from '../../types';
import { CRYPTO_TRANSFERS_ASSET_ITEMS_PER_PAGE } from '../../../config';

interface TransfersAssetListContextProps {
  assetsListForDeposit: TAssetForTransferArray;
  assetsListForSend: TAssetForTransferArray;
  walletHosts: CryptoTransfersVASPQuery$data['cryptoTransfersVASP'];
  isAssetsListLoading: boolean;
  onInputChange: (input: string) => void;
  assetFilterSearchValue: string;
  clearSearchParams: () => void;
  canLoadMore: boolean;
  loadMore: () => void;
}

const DEBOUNCE_DELAY_AMOUNT_MS = 500;

const TransfersAssetListContext =
  createContext<TransfersAssetListContextProps | null>(null);

export function useTransfersAssetsListContext() {
  const contextValue = useContext(TransfersAssetListContext);

  if (contextValue === null) {
    throw new Error(
      'useTransfersAssetsListContext must be used within a TransfersAssetListContextProvider',
    );
  }

  return contextValue;
}

interface TransfersAssetListContextProviderProps extends PropsWithChildren {
  initialData: NonNullable<TAssetsForTransfersResponse>;
  walletHosts: CryptoTransfersVASPQuery$data['cryptoTransfersVASP'];
}

export const TransfersAssetListContextProvider = ({
  initialData,
  walletHosts,
  children,
}: TransfersAssetListContextProviderProps) => {
  const [fullAssetData, setFullAssetData] = useState(initialData);

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const [assetFilterSearchValue, setAssetFilterSearchValue] = useState('');

  const [loading, setLoading] = useState(false);

  const initialSendAssets = useMemo(
    () => extractDepositAndSellAssets(initialData).assetsListForSend,

    [],
  );

  const filteredSendAssets = useMemo(() => {
    if (assetFilterSearchValue.trim().length >= MINIMUM_SEARCH_TERM_LENGTH) {
      const searchTerm = assetFilterSearchValue.toLowerCase().trim();

      return initialSendAssets.filter(
        (asset) =>
          asset?.name?.toLowerCase().includes(searchTerm) ||
          (asset?.symbol && asset.symbol.toLowerCase().includes(searchTerm)),
      );
    }

    return initialSendAssets;
  }, [initialSendAssets, assetFilterSearchValue]);

  const { assetsListForDeposit, canLoadMore, endCursorId } = useMemo(() => {
    const processed = extractDepositAndSellAssets(fullAssetData);

    // Type guard to check if fullAssetData has assets property (deposit response)
    const pageInfo =
      'assets' in fullAssetData && fullAssetData.assets
        ? fullAssetData.assets.pageInfo
        : null;

    return {
      assetsListForDeposit: processed.assetsListForDeposit,
      canLoadMore: pageInfo?.hasNextPage ?? false,
      endCursorId: pageInfo?.endCursor ?? null,
    };
  }, [fullAssetData]);

  const handleSearch = useCallback(async (input: string) => {
    const sanitizedInput = input.trim();

    if (sanitizedInput.length < MINIMUM_SEARCH_TERM_LENGTH) {
      setFullAssetData(initialData);

      setLoading(false);

      return;
    }

    try {
      const { data, errors } = await getPaginatedAssetsForTransfer({
        searchTerm: sanitizedInput,
        first: CRYPTO_TRANSFERS_ASSET_ITEMS_PER_PAGE,
      });

      if (errors) {
        processErrors(errors);

        return;
      }

      setFullAssetData(data || EmptyPaginatedAssetsForTransfersResponse);
    } catch (error) {
      const errorString = JSON.stringify(error);

      processErrors([errorString]);

      datadogErrorHelper({
        errorMessage:
          'Error fetching more assets by searchTerm: ' + errorString,
        errorSeverity: DatadogErrorLevels.HIGH,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearchRef = useRef(
    debounce(handleSearch, DEBOUNCE_DELAY_AMOUNT_MS),
  );

  const loadMore = useCallback(async () => {
    if (!canLoadMore || !endCursorId) return;

    try {
      const { data: responseData, errors } =
        await getPaginatedAssetsForTransfer({
          ...getPaginatedAssetsForTransferInitialQueryParams,
          after: endCursorId,
        });

      if (errors) {
        processErrors(errors);

        return;
      }

      if (!responseData) return;

      setFullAssetData((prevData) => ({
        assetGroups: responseData.assetGroups?.length
          ? responseData.assetGroups
          : 'assetGroups' in prevData
            ? prevData.assetGroups
            : [],
        assets: {
          edges: [
            ...('assets' in prevData && prevData.assets?.edges
              ? prevData.assets.edges
              : []),
            ...(responseData.assets?.edges ?? []),
          ],
          totalCount: responseData.assets?.totalCount ?? 0,
          pageInfo:
            responseData.assets?.pageInfo ??
            EmptyPaginatedAssetsForTransfersResponse.assets.pageInfo,
        },
      }));
    } catch (error) {
      const errorString = JSON.stringify(error);

      processErrors([errorString]);

      datadogErrorHelper({
        errorMessage: 'Error fetching more assets: ' + errorString,
        errorSeverity: DatadogErrorLevels.HIGH,
      });
    }
  }, [canLoadMore, endCursorId]);

  const onInputChange = useCallback(
    (newValue: string) => {
      setAssetFilterSearchValue(newValue);

      setLoading(true);

      debouncedSearchRef.current(newValue);
    },
    [debouncedSearchRef],
  );

  const clearSearchParams = useCallback(() => {
    setLoading(true);

    setAssetFilterSearchValue('');

    debouncedSearchRef.current('');
  }, [debouncedSearchRef]);

  const contextValue = useMemo(
    () => ({
      assetsListForDeposit,
      assetsListForSend: filteredSendAssets,
      walletHosts,
      isAssetsListLoading: loading,
      assetFilterSearchValue,
      onInputChange,
      clearSearchParams,
      canLoadMore,
      loadMore,
    }),
    [
      assetsListForDeposit,
      filteredSendAssets,
      walletHosts,
      loading,
      assetFilterSearchValue,
      onInputChange,
      clearSearchParams,
      canLoadMore,
      loadMore,
    ],
  );

  return (
    <TransfersAssetListContext.Provider value={contextValue}>
      {children}
    </TransfersAssetListContext.Provider>
  );
};
